import {useCallback, useEffect, useRef, useState} from 'react'

import {useDecision} from '@s-ui/pde'
import {useSuiContext} from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {MESSAGE_ROLES} from '../config.js'
import {useNetLoading} from '../context/NetLoadingContext.js'
import {getAssistantErrorMessage, isLastMessageAnError, mapChatContentWithFavorites} from '../helpers.js'
import {getTrackingAnswerClicked, getTrackingReviewSent} from '../tracking.js'

const getFavorites = domain =>
  domain
    .get('get_user_favorites_use_case')
    .execute()
    .catch(() => null)

export function useNetChat({
  context,
  i18n,
  initialChatId,
  isFullscreen = false,
  isOpen,
  listingSectionId,
  pageName,
  onClose,
  onFavoriteChange,
  onReviewNegative,
  onReviewPositive
} = {}) {
  const {domain} = useSuiContext()
  const {setNetLoading} = useNetLoading()
  const {push: routerPush} = useRouter()
  const {track} = useAnalyticsTrack()

  const {enabled: isAiAssistantEnabled, variationKey: aiAssistantVariant} = useDecision(
    'ff_backend_buyer_ai_assistant',
    {attributes: {page_name: pageName}}
  )

  const [chatData, setChatData] = useState({})
  const [chats, setChats] = useState([])
  const [retrying, setRetrying] = useState(false)
  const [restoring, setRestoring] = useState(true)
  const [isStorageReady, setIsStorageReady] = useState(false)
  const [notificationError, setNotificationError] = useState(false)
  const [selectedReviewingBlock, setSelectedReviewingBlock] = useState({})

  const lastActiveChatIdRef = useRef(null)
  const activeRequestsRef = useRef(new Set())

  const config = domain.get('config')
  const tracking = config.get('tracking')
  const {TRACKING_EVENT_NAMES: {NET_ASSISTANT_REVIEW_SENT} = {}} = tracking || {}
  const {FEEDBACK_REACTION_TYPES, NET_SUGGESTION_TYPE} = config.get('COMMON').AI_ASSISTANT
  const {NOT_FOUND_CHAT_ASSISTANT_ERROR: NotFoundAssistantError} = config.get('ERRORS') || {}

  // Restore persisted chat once on mount
  useEffect(() => {
    const restore = async () => {
      const [, chatList] = await domain.get('get_all_assistant_chats_use_case').execute({resetLoading: true})
      setIsStorageReady(true)

      if (!chatList?.length) {
        setRestoring(false)
        return
      }

      setChats(chatList)

      const chatIdToLoad = initialChatId ?? lastActiveChatIdRef.current ?? (isFullscreen ? null : chatList[0].id)
      if (!chatIdToLoad) {
        setRestoring(false)
        return
      }

      const [loadError, saved] = await domain.get('load_assistant_chat_use_case').execute({chatId: chatIdToLoad})
      setIsStorageReady(true)

      if (loadError || !saved?.chatId) {
        setRestoring(false)
        return
      }

      setChatData({chatId: saved.chatId, content: []})
      setRestoring(true)

      const [checkError, validated] = await domain
        .get('get_existing_assisant_chat_use_case')
        .execute({chatId: saved.chatId})

      if (checkError || !validated?.exists) {
        if (checkError instanceof NotFoundAssistantError) {
          await clearChat({chatId: saved.chatId})
        } else {
          if (checkError) setNotificationError(true)
          setChatData({})
        }
        setRestoring(false)
        return
      }

      const favorites = await getFavorites(domain)
      setChatData(prev => {
        const restoredContent = mapChatContentWithFavorites(saved.content, favorites?.ads ?? [])
        const lastMessage = restoredContent[restoredContent.length - 1]
        const isAwaitingResponse = lastMessage?.role === MESSAGE_ROLES.USER
        return {
          ...prev,
          chatId: saved.chatId,
          content: isAwaitingResponse ? [...restoredContent, getAssistantErrorMessage(i18n)] : restoredContent
        }
      })

      setRestoring(false)
    }

    restore()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) {
      if (chatData?.chatId) lastActiveChatIdRef.current = chatData.chatId
    }
  }, [isOpen, chatData?.chatId])

  // Update global loading state when hook is processing
  useEffect(() => {
    const hasLoadingMessages = chatData.content?.some(message => message.loading)
    const isProcessing = retrying || hasLoadingMessages
    setNetLoading(isProcessing)
  }, [retrying, chatData.content, setNetLoading])

  // Refresh chats list when another tab adds or modifies a conversation
  useEffect(() => {
    const handleStorage = event => {
      if (event.key !== 'net_chat:chats_list') return
      domain
        .get('get_all_assistant_chats_use_case')
        .execute()
        .then(([, updated]) => {
          if (updated) setChats(updated)
        })
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [domain])

  // Listen for storage changes from other tabs for the same active conversation
  useEffect(() => {
    if (!chatData?.chatId) return

    const handleStorage = async event => {
      if (event.key !== `net_chat:chat:${chatData.chatId}`) return

      const [, saved] = await domain.get('load_assistant_chat_use_case').execute({chatId: chatData.chatId})

      if (!saved) return

      const hasOnlyUserMessages =
        saved.content.length > 0 && saved.content[saved.content.length - 1].role === MESSAGE_ROLES.USER

      if (hasOnlyUserMessages) {
        saved.content.push({role: MESSAGE_ROLES.ASSISTANT, loading: true})
      }

      setChatData(saved)
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [domain, chatData?.chatId])

  const cleanLoadingMessage = (content = []) => content.filter(message => !message.loading)

  const _saveChatAndGetAllUpdated = useCallback(
    (newChatContent, onSavedChat) => {
      // A chat that never got an id (the very first message failed) cannot be reopened: saving it
      // leaves a history entry that loads `/assistant/chats/undefined`.
      if (!newChatContent?.chatId) return

      domain
        .get('save_assistant_chat_use_case')
        .execute(newChatContent)
        .then(() => {
          // Refresh chats list to show/clear loading state
          domain
            .get('get_all_assistant_chats_use_case')
            .execute()
            .then(([, updatedList]) => {
              if (updatedList) setChats(updatedList)
            })

          onSavedChat?.()
        })
    },
    [domain]
  )

  const createMessage = useCallback(
    async (value, chatId, chatSnapshot) => {
      const requestKey = chatId ?? '__new__'
      activeRequestsRef.current.add(requestKey)

      // Fetch assistant response
      const useCase = chatId ? 'update_assisant_chat_use_case' : 'start_assistant_new_chat_use_case'
      const params = chatId
        ? {chatId, text: value, context, ...(isAiAssistantEnabled && {variant: aiAssistantVariant})}
        : {text: value, context, ...(isAiAssistantEnabled && {variant: aiAssistantVariant})}
      const [error, response] = await domain.get(useCase).execute(params)

      if (error) {
        setChatData(prev => {
          if (prev?.chatId !== chatId) return prev
          const errorMessage = isLastMessageAnError(prev?.content) ? [] : [getAssistantErrorMessage(i18n)]
          const next = {...prev, chatId, content: [...cleanLoadingMessage(prev?.content), ...errorMessage]}
          _saveChatAndGetAllUpdated(next)
          return next
        })
      } else {
        const {chatId: responseChatId, ...chatContent} = response
        const favorites = await getFavorites(domain)
        setChatData(prev => {
          if (prev?.chatId !== chatId) {
            // Chat switched while in-flight: persist the response using the snapshot
            // saved before the request, so it's available when navigating back.
            const updated = [...chatSnapshot.content, {role: MESSAGE_ROLES.ASSISTANT, ...chatContent}]
            _saveChatAndGetAllUpdated({...chatSnapshot, chatId: responseChatId, content: updated})
            return prev
          }
          const cleaned = cleanLoadingMessage(prev?.content)
          const updated = [...cleaned, {role: MESSAGE_ROLES.ASSISTANT, ...chatContent}]
          const withFavorites = mapChatContentWithFavorites(updated, favorites?.ads ?? [])
          const next = {...prev, chatId: responseChatId, content: withFavorites}

          _saveChatAndGetAllUpdated(next, () => {
            window.dispatchEvent(new StorageEvent('storage', {key: `net_chat:chat:${responseChatId}`}))
          })
          return next
        })
      }

      activeRequestsRef.current.delete(requestKey)
    },
    [context, isAiAssistantEnabled, aiAssistantVariant, domain, i18n, _saveChatAndGetAllUpdated]
  )

  const sendMessage = useCallback(
    async (value, messageType, {onTrack} = {}) => {
      setChatData(prev => {
        const {chatId} = prev || {}

        // Optimistically add user message + loading skeleton
        const newContent = [
          ...(prev?.content || []),
          {role: MESSAGE_ROLES.USER, text: value},
          {role: MESSAGE_ROLES.ASSISTANT, loading: true}
        ]
        const next = {...prev, chatId, content: newContent}

        onTrack?.(next, {value, messageType})

        // Save user message (without skeleton) so other tabs pick it up via storage event
        const contentWithoutLoading = newContent.filter(message => !message.loading)
        const chatSnapshot = {...prev, chatId, content: contentWithoutLoading}
        if (chatId) {
          _saveChatAndGetAllUpdated(chatSnapshot, () => {
            createMessage(value, chatId, chatSnapshot)
          })
        } else {
          createMessage(value, undefined, chatSnapshot)
        }

        return next
      })
      setRetrying(false)
    },
    [_saveChatAndGetAllUpdated, createMessage]
  )

  const retry = useCallback(async () => {
    setRetrying(true)
    const lastUserMessage = chatData?.content?.findLast(m => m.role === MESSAGE_ROLES.USER)
    if (lastUserMessage) {
      const {chatId} = chatData || {}
      const cleaned = cleanLoadingMessage(chatData?.content ?? [])
      const contentWithoutError = isLastMessageAnError(cleaned) ? cleaned.slice(0, -1) : cleaned
      const chatSnapshot = {...chatData, chatId, content: contentWithoutError}
      _saveChatAndGetAllUpdated(chatSnapshot)

      setChatData(prev => {
        const cleaned = cleanLoadingMessage(prev?.content)
        const withoutError = isLastMessageAnError(cleaned) ? cleaned.slice(0, -1) : cleaned
        return {...prev, chatId, content: [...withoutError, {role: MESSAGE_ROLES.ASSISTANT, loading: true}]}
      })

      await createMessage(lastUserMessage.text, chatId, chatSnapshot)
      setRetrying(false)
    }
  }, [chatData, createMessage])

  const clearChat = useCallback(
    async ({chatId} = {}) => {
      await domain.get('clear_assistant_chat_use_case').execute({chatId})
      setChatData({})
      window.dispatchEvent(new StorageEvent('storage', {key: 'net_chat:chats_list'}))
    },
    [domain]
  )

  const loadChat = useCallback(
    async chatId => {
      setChatData({chatId})
      setRestoring(true)
      const [checkError, validated] = await domain.get('get_existing_assisant_chat_use_case').execute({chatId})

      if (checkError || !validated?.exists) {
        if (NotFoundAssistantError && checkError instanceof NotFoundAssistantError) {
          await clearChat({chatId})
        } else {
          if (checkError) setNotificationError(true)
          setChatData({})
        }
        setRestoring(false)
        return
      }

      const [, saved] = await domain.get('load_assistant_chat_use_case').execute({chatId})
      if (saved) {
        const favorites = await getFavorites(domain)
        const content = mapChatContentWithFavorites(saved.content, favorites?.ads ?? [])
        const lastMessage = content[content.length - 1]
        const isAwaitingResponse = lastMessage?.role === MESSAGE_ROLES.USER
        const hasActiveRequest = activeRequestsRef.current.has(chatId)
        let resolvedContent
        if (isAwaitingResponse) {
          resolvedContent = hasActiveRequest
            ? [...content, {role: MESSAGE_ROLES.ASSISTANT, loading: true}]
            : [...content, getAssistantErrorMessage(i18n)]
        } else {
          resolvedContent = content
        }
        setChatData({...saved, content: resolvedContent})
      }
      setRestoring(false)
    },
    [domain, clearChat, NotFoundAssistantError]
  )

  const refreshFavorites = useCallback(async () => {
    const favorites = await getFavorites(domain)
    setChatData(prev => {
      const newContent = mapChatContentWithFavorites(prev.content, favorites?.ads ?? [])
      const next = {...prev, content: newContent}
      domain.get('save_assistant_chat_use_case').execute(next)
      return next
    })
    return favorites?.ads ?? []
  }, [domain])

  const markMessageReaction = useCallback(
    (messageId, reaction) => {
      setChatData(prev => {
        const newContent = prev?.content?.map(message =>
          message.messageId === messageId ? {...message, reaction} : message
        )
        const next = {...prev, content: newContent}
        domain.get('save_assistant_chat_use_case').execute(next)
        return next
      })
    },
    [domain]
  )

  const markBlockSearchSaved = useCallback(
    ({messageId, blockIndex}) => {
      setChatData(prev => {
        const messageIndex = prev?.content?.findIndex(m => m.messageId === messageId)
        if (messageIndex === -1 || !prev?.content?.[messageIndex]?.blocks) return prev
        const newContent = [...prev.content]
        newContent[messageIndex] = {
          ...newContent[messageIndex],
          blocks: newContent[messageIndex].blocks.map((block, idx) =>
            idx === blockIndex ? {...block, searchSaved: true} : block
          )
        }
        const next = {...prev, content: newContent}
        domain.get('save_assistant_chat_use_case').execute(next)
        return next
      })
    },
    [domain]
  )

  // --- Event handlers ---

  const onAdClick = (adId, event) => {
    track(getTrackingAnswerClicked(tracking, chatData, adId))

    const {type, adId: currentAdId} = context?.page ?? {}
    const isCurrentAd = type === NET_SUGGESTION_TYPE.AD_DETAIL && String(currentAdId) === String(adId)
    const isModifiedClick = event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey

    if (onClose && isCurrentAd && !isModifiedClick) {
      event?.preventDefault()
      onClose()
    }
  }

  const onShowResults = async block => {
    track(getTrackingAnswerClicked(tracking, chatData))

    const [error, result] = await domain.get('get_url_params_search_use_case').execute({
      search: block.filters,
      listingSectionId: block.listingSectionId || listingSectionId,
      pageName
    })

    if (error) {
      setNotificationError(true)
    } else {
      onClose?.()
      routerPush(result.url)
    }
  }

  const onSearchSaved = ({messageId, blockIndex}) => {
    markBlockSearchSaved({messageId, blockIndex})
  }

  const onClickFavorite = async () => {
    const fetchedFavoritesAds = await refreshFavorites()
    onFavoriteChange?.(fetchedFavoritesAds)
  }

  const onReviewSuccess = (messageId, type, step, block) => {
    markMessageReaction(messageId, type)

    domain
      .get('get_event_tracking_data_use_case')
      .execute({
        event: NET_ASSISTANT_REVIEW_SENT,
        filters: block.filters,
        ads: block?.response?.items || []
      })
      .then(({data: extendedTrackingData}) => {
        const reviewingBlockTrackingData = {...extendedTrackingData, step, messageId, chatId: chatData?.chatId}
        track(
          getTrackingReviewSent(tracking, {
            ...reviewingBlockTrackingData,
            type: type === FEEDBACK_REACTION_TYPES.POSITIVE ? 'positiva' : 'negativa'
          })
        )

        if (type === FEEDBACK_REACTION_TYPES.NEGATIVE) {
          setSelectedReviewingBlock(reviewingBlockTrackingData)
          onReviewNegative?.()
        } else {
          onReviewPositive?.()
        }
      })
  }

  const clearSelectedReviewingBlock = () => setSelectedReviewingBlock({})

  return {
    chatData,
    chats,
    retrying,
    restoring,
    isStorageReady,
    sendMessage,
    retry,
    loadChat,
    clearChat,
    refreshFavorites,
    markMessageReaction,
    markBlockSearchSaved,
    onAdClick,
    onShowResults,
    onSearchSaved,
    onClickFavorite,
    onReviewSuccess,
    selectedReviewingBlock,
    clearSelectedReviewingBlock,
    notificationError,
    clearNotificationError: () => setNotificationError(false)
  }
}
